/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useStore } from "../contexts/StoreContext";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchBranches, fetchPasses } from "../api/queries";
import GeneralSection from "./settings/GeneralSection";
import BrandingSection from "./settings/BrandingSection";
import BranchesSection from "./settings/BranchesSection";
import PlanSection from "./settings/PlanSection";
import DeleteStoreSection from "./settings/DeleteStoreSection";

const SECTIONS = ["general", "branding", "branches", "billing", "danger"];

export default function Settings() {
  const { selectedStore } = useStore();
  if (!selectedStore?.id) return null;
  // Keyed on the id: switching store from the header remounts every section,
  // so no form keeps the previous store's values. A background ["stores"]
  // refetch keeps the id and therefore keeps unsaved edits.
  return <StoreSettings key={selectedStore.id} store={selectedStore} />;
}

function StoreSettings({ store }) {
  const { t } = useLanguage();
  const [section, setSection] = useState("general");
  const tabRefs = useRef({});

  const { data: branches = [] } = useQuery({
    queryKey: ["branches", store.id],
    queryFn: () => fetchBranches(store.id),
    enabled: !!store.id,
  });
  // Only the Plan tab shows the card count, so the full passes list is not
  // fetched until that tab is opened (same key as Passes.jsx, shared cache).
  const { data: passes } = useQuery({
    queryKey: ["passes", store.id],
    queryFn: () => fetchPasses(store.id),
    enabled: !!store.id && section === "billing",
  });

  const select = (next, focus = false) => {
    setSection(next);
    if (focus) tabRefs.current[next]?.focus();
  };

  const onTabKeyDown = (e) => {
    const index = SECTIONS.indexOf(section);
    const moves = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
    if (e.key in moves) {
      e.preventDefault();
      select(
        SECTIONS[(index + moves[e.key] + SECTIONS.length) % SECTIONS.length],
        true,
      );
    } else if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      select(
        e.key === "Home" ? SECTIONS[0] : SECTIONS[SECTIONS.length - 1],
        true,
      );
    }
  };

  // Every panel stays mounted (hidden when inactive), so switching sections
  // keeps unsaved edits and an open branch form.
  const panels = {
    general: <GeneralSection store={store} />,
    branding: <BrandingSection store={store} />,
    branches: <BranchesSection store={store} branches={branches} />,
    billing: (
      <PlanSection
        store={store}
        cardCount={Array.isArray(passes) ? passes.length : undefined}
        branchCount={branches.length}
      />
    ),
    danger: (
      <DeleteStoreSection
        store={store}
        onGoToBilling={() => select("billing", true)}
      />
    ),
  };

  return (
    <div className="flex max-w-[1240px] flex-col gap-5">
      <header className="flex min-w-0 flex-col gap-1.5">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-[28px]">
          {t("settings.title")}
        </h1>
        <p className="truncate text-[15px] text-gray-700">{store.name}</p>
      </header>

      <div className="flex flex-wrap items-start gap-6">
        <div
          role="tablist"
          aria-label={t("settings.sections.label")}
          aria-orientation="vertical"
          onKeyDown={onTabKeyDown}
          className="flex max-w-full flex-[1_1_200px] flex-col gap-0.5"
        >
          {SECTIONS.map((id) => {
            const on = section === id;
            return (
              <button
                key={id}
                ref={(el) => (tabRefs.current[id] = el)}
                type="button"
                role="tab"
                id={`settings-tab-${id}`}
                aria-selected={on}
                aria-controls={`settings-panel-${id}`}
                tabIndex={on ? 0 : -1}
                onClick={() => select(id)}
                className={`min-h-11 rounded-[10px] px-3.5 text-left text-sm ${
                  on
                    ? "bg-white font-semibold shadow-sm"
                    : "font-medium hover:bg-white/60"
                } ${id === "danger" ? "text-red-700" : on ? "text-gray-900" : "text-gray-600"}`}
              >
                {t(`settings.sections.${id}`)}
              </button>
            );
          })}
        </div>

        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-4">
          {SECTIONS.map((id) => (
            <div
              key={id}
              role="tabpanel"
              id={`settings-panel-${id}`}
              aria-labelledby={`settings-tab-${id}`}
              hidden={section !== id}
            >
              {panels[id]}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
